import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Output,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  MeetingLinkSelection,
  SendMeetingBotRequest,
} from '@interfaces/meetings/requests/meeting.request';
import { MeetingDto } from '@interfaces/meetings/responses/meeting.response';
import { TranslateService } from '@ngx-translate/core';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { MeetingsHttpService } from '@services/http/meetings/meetings-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { first } from 'rxjs';
import { MeetingLinkFieldsComponent } from '@features/dashboard/meetings/components/meeting-link-fields/meeting-link-fields.component';
import {
  mensagemDoErro,
  montarHorarioDoBot,
} from '@features/dashboard/meetings/components/meeting-presenter';

/**
 * "Adicionar bot na call" (card 3134): manda o bot de gravação para uma reunião que não está na
 * agenda de ninguém conectado — o time cola o link.
 *
 * O bot entra agora ou num horário (dia pelo `app-calendar` + hora `HH:mm`, no fuso de São
 * Paulo). Os vínculos são opcionais e em cascata (card 3138, `app-meeting-link-fields`): empresa →
 * parceiro → deal e relacionamento bancário → contato; escolhidos, a ligação já nasce vinculada,
 * e uma combinação inválida é recusada antes de criar o bot. Link inválido, horário
 * no passado e bot já ativo na mesma call são recusados pelo backend, e a mensagem dele vai no toast.
 */
@Component({
  selector: 'app-modal-send-meeting-bot',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule, MeetingLinkFieldsComponent],
  templateUrl: './modal-send-meeting-bot.component.html',
  styleUrl: './modal-send-meeting-bot.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalSendMeetingBotComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;
  @ViewChild('campos') campos: MeetingLinkFieldsComponent | null = null;

  /** A ligação criada — a tela recarrega a lista e os agendamentos. */
  @Output() enviou = new EventEmitter<MeetingDto>();

  protected readonly gravando = signal(false);
  protected readonly agendar = signal(false);
  protected readonly hoje = signal<Date | null>(null);
  protected readonly vinculos = signal<MeetingLinkSelection>({});

  /** Bindings (ngModel). */
  protected link = '';
  protected titulo = '';
  protected dia: string | null = null;
  protected hora = '';

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    translateService: TranslateService,
    private readonly meetingsService: MeetingsHttpService,
    private readonly dateFormatter: DateFormatterService,
    private readonly toast: ToastHelperService
  ) {
    super(translateService);
  }

  open(): void {
    this.link = '';
    this.titulo = '';
    this.dia = null;
    this.hora = '';
    this.vinculos.set({});
    this.campos?.reiniciar();
    this.agendar.set(false);
    this.gravando.set(false);
    this.hoje.set(this.dateFormatter.getTodayDate());

    this.dialogRef?.open('', '', '', 'Cancelar', '', 'send-meeting-bot', false, true);
  }

  protected alternarAgendamento(ligado: boolean): void {
    this.agendar.set(ligado);
    if (!ligado) {
      this.dia = null;
      this.hora = '';
    }
  }

  isFormValid(): boolean {
    if (this.gravando() || !this.link.trim()) return false;
    return !this.agendar() || montarHorarioDoBot(this.dia, this.hora) !== null;
  }

  submitAction(): void {
    if (!this.isFormValid()) return;

    const payload: SendMeetingBotRequest = {
      meetingUrl: this.link.trim(),
      title: this.titulo.trim() || null,
      joinAt: this.agendar() ? montarHorarioDoBot(this.dia, this.hora) : null,
      ...this.vinculos(),
    };

    this.gravando.set(true);
    this.meetingsService
      .sendBot(payload)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: criada => {
          this.gravando.set(false);
          this.toast.showToastSuccess(
            this.agendar()
              ? 'Bot agendado. Ele entra na reunião no horário escolhido.'
              : 'Bot enviado. Admita o "Cog AI bot" na reunião, se ele ficar na sala de espera.'
          );
          if (criada) this.enviou.emit(criada);
          this.fechar();
        },
        error: erro => {
          this.gravando.set(false);
          this.toast.showToastError(
            'Não foi possível enviar o bot',
            mensagemDoErro(erro, 'Tente de novo em alguns instantes.')
          );
        },
      });
  }

  fechar(): void {
    this.dialogRef?.close();
  }
}
